'use client';

import dynamic from 'next/dynamic';
import Skeleton from '@mui/material/Skeleton';
import { Box } from '@/primitives/Box';
import { Stack } from '@/primitives/Stack';
import { Typography } from '@/primitives/Typography';
import { landingHerdEveryAngleSection } from './homeLandingContent';
import { sectionShell } from './homeLandingSectionSx';
import { landingDemo } from './landingDemoChrome';

const paneFallback = (
  <Skeleton
    variant="rounded"
    sx={{
      height: 360,
      bgcolor: `${landingDemo.ink}14`,
      borderRadius: 0,
    }}
  />
);

const LandingPedigreeShowcase = dynamic(
  () => import('./LandingPedigreeShowcase'),
  { ssr: false, loading: () => paneFallback },
);

const LandingHerdCompositionShowcase = dynamic(
  () => import('./LandingHerdCompositionShowcase'),
  { ssr: false, loading: () => paneFallback },
);

/**
 * Pedigree + herd composition demos. Section is hardcoded dark (marketing
 * reference) so light-mode app chrome cannot wash out the demos.
 */
export function LandingHerdEveryAngle() {
  const { eyebrow, titleLead, titleAccent, body } =
    landingHerdEveryAngleSection;

  return (
    <Box
      component="section"
      aria-labelledby="landing-herd-every-angle-title"
      sx={{
        py: { xs: 8, md: 10 },
        backgroundColor: landingDemo.sectionBg,
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 720px',
      }}
    >
      <Box sx={{ ...sectionShell, position: 'relative', zIndex: 1 }}>
        <Stack
          spacing={1}
          sx={{
            alignItems: 'flex-start',
            mb: { xs: 4, md: 5 },
            textAlign: 'left',
            maxWidth: 720,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              letterSpacing: '0.06em',
              fontSize: '0.75rem',
              color: landingDemo.accent,
            }}
            variant="labelmedium"
          >
            {eyebrow}
          </Typography>
          <Typography
            component="h2"
            id="landing-herd-every-angle-title"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.015em',
              lineHeight: 1.08,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              textWrap: 'balance',
              color: landingDemo.ink,
            }}
          >
            {titleLead}
            <Box
              component="span"
              sx={{
                display: 'block',
                background: `linear-gradient(90deg, ${landingDemo.accentStrong} 0%, ${landingDemo.accent} 100%)`,
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {titleAccent}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              lineHeight: 1.6,
              color: landingDemo.inkSoft,
            }}
          >
            {body}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 0,
            border: `1px solid ${landingDemo.panelBorder}`,
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: landingDemo.panelBg,
            boxShadow: `0 16px 40px ${landingDemo.sectionBg}cc`,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRight: {
                xs: 'none',
                md: `1px solid ${landingDemo.divider}`,
              },
              borderBottom: {
                xs: `1px solid ${landingDemo.divider}`,
                md: 'none',
              },
              minHeight: 360,
            }}
          >
            <LandingPedigreeShowcase />
          </Box>
          <Box sx={{ position: 'relative', minHeight: 360 }}>
            <LandingHerdCompositionShowcase />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
